import { useCallback, useEffect, useMemo } from 'react'

import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { type ReportAccessGrant } from '@webapp/platform/iam/entities/ReportAccessGrant'
import messages from './messages'
import { useQueryReportAccessGrants } from '../../models/useQueryReportAccessGrants'
import { type ReportAccessGrantTagViewModel } from '../../types'

export interface IUseReportAccessGrantListPresenterParams {
  secondaryCategoryId: number
  highlightedGrantId: number | undefined
  isGrantCommandPending: boolean
  onRemoveGrant: (
    grant: ReportAccessGrant,
    options: { isLastGrant: boolean }
  ) => void
  onClearAll: (grants: ReportAccessGrant[]) => void
  onRetry: () => void
}

export const useReportAccessGrantListPresenter = ({
  secondaryCategoryId,
  highlightedGrantId,
  isGrantCommandPending,
  onRemoveGrant,
  onClearAll,
  onRetry,
}: IUseReportAccessGrantListPresenterParams) => {
  const intl = useProcurifyIntl()

  const { grants, isLoading, error, refetch } =
    useQueryReportAccessGrants(secondaryCategoryId)

  useEffect(() => {
    if (error) {
      toast.error({
        title: intl.formatMessage(messages.fetchErrorToastTitle),
        children:
          getMessageFromError(error) ??
          intl.formatMessage(messages.fetchErrorToastMessage),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  const grantTags = useMemo<ReportAccessGrantTagViewModel[]>(
    () =>
      grants.map((grant) => ({
        id: grant.id,
        userFullName: grant.userFullName,
        isHighlighted: grant.id === highlightedGrantId,
      })),
    [grants, highlightedGrantId]
  )

  const handleRemoveGrant = useCallback(
    (grantId: number) => {
      const grant = grants.find(({ id }) => id === grantId)

      if (grant) onRemoveGrant(grant, { isLastGrant: grants.length === 1 })
    },
    [grants, onRemoveGrant]
  )

  const handleClearAll = useCallback(
    () => onClearAll(grants),
    [grants, onClearAll]
  )

  const handleRetry = useCallback(() => {
    onRetry()
    refetch()
  }, [onRetry, refetch])

  const hasGrants = grants.length > 0
  // A failed load must never read as "no one has access"; a failed refetch
  // over cached grants keeps showing them
  const showErrorState = Boolean(error) && !hasGrants

  return {
    data: {
      grantTags,
      clearAllCount: grants.length,
    },
    states: {
      showInitialLoader: isLoading,
      showErrorState,
      showEmptyState: !showErrorState && !hasGrants,
      showClearAll: !showErrorState,
      isClearAllDisabled: !hasGrants || isGrantCommandPending,
    },
    methods: {
      onRemoveGrant: handleRemoveGrant,
      onClearAll: handleClearAll,
      onRetry: handleRetry,
    },
  }
}
